import { useMemo } from 'react'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  endOfToday,
  endOfYesterday,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns'
import moment from 'moment'
import { PREFERRED_DATE_FORMAT } from 'store/store'

import { utcToZonedTime } from './time-zone/helpers'

export const defaultDateFormat = 'dd/MM/yyyy'
export const defaultTimeFormat = 'HH:mm:ss'
export const defaultDateTimeFormat = `${defaultDateFormat} ${defaultTimeFormat}`
export const API_DATE_FORMAT = 'yyyy-MM-dd'

export const datePattern = /\d{4}-[01]\d-[0-3]\d/
export const dateTimePattern =
  /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/

export function isValidDate(maybeDate: Date | string): boolean {
  return moment(maybeDate).isValid()
}

export function useDifferenceInFullDays(dateRight: string) {
  const currentTime = new Date()

  // Uncomment this if you want to update the time like a live counter
  // const [currentTime, setCurrentTime] = useState(new Date())

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentTime(new Date())
  //   }, 1000)

  //   return () => clearInterval(interval)
  // }, [])

  return useMemo(() => {
    const targetDate = new Date(dateRight)
    const diffDays = differenceInDays(currentTime, targetDate)
    const diffHours = differenceInHours(currentTime, targetDate) - diffDays * 24
    const diffMinutes =
      differenceInMinutes(currentTime, targetDate) - (diffDays * 24 * 60 + diffHours * 60)
    const diffSeconds =
      differenceInMinutes(currentTime, new Date(dateRight)) < 1
        ? differenceInSeconds(currentTime, new Date(dateRight)) % 60
        : 0

    const pluralS = (number: number) => (number !== 1 ? 's' : '')

    let totalDifference = ''

    if (diffDays !== 0) totalDifference += `${diffDays} day${pluralS(diffDays)} `
    if (diffHours !== 0) totalDifference += `${diffHours} hour${pluralS(diffHours)} `
    if (diffMinutes !== 0) totalDifference += `${diffMinutes} minute${pluralS(diffMinutes)} `
    if (diffSeconds !== 0) totalDifference += `${diffSeconds} second${pluralS(diffSeconds)} `

    return totalDifference + 'ago'
  }, [dateRight, currentTime])
}

export function createDateAsUTC(date: Date | string, skipTime?: boolean, endOfDay?: boolean): Date {
  const dateFormat = date instanceof Date ? date : new Date(date)
  if (skipTime) {
    return new Date(Date.UTC(dateFormat.getFullYear(), dateFormat.getMonth(), dateFormat.getDate()))
  }
  if (endOfDay) {
    return new Date(
      Date.UTC(
        dateFormat.getFullYear(),
        dateFormat.getMonth(),
        dateFormat.getDate(),
        23,
        59,
        59,
        999
      )
    )
  }
  return new Date(
    Date.UTC(
      dateFormat.getFullYear(),
      dateFormat.getMonth(),
      dateFormat.getDate(),
      dateFormat.getHours(),
      dateFormat.getMinutes(),
      dateFormat.getSeconds()
    )
  )
}

export function convertDateToUTC(date: Date | string): Date {
  const dateFormat = date instanceof Date ? date : new Date(date)
  return new Date(
    dateFormat.getUTCFullYear(),
    dateFormat.getUTCMonth(),
    dateFormat.getUTCDate(),
    dateFormat.getUTCHours(),
    dateFormat.getUTCMinutes(),
    dateFormat.getUTCSeconds()
  )
}

export const formatLocalDateUTC = (
  date: string | null | undefined | Date,
  withTime?: boolean
): string | null => {
  const dateFormat = localStorage.getItem(PREFERRED_DATE_FORMAT)
  const userFormatDate = getDateTimeFormat()

  if (!date) {
    return null
  }
  if (date instanceof Date) {
    if (withTime) {
      return format(date, userFormatDate)
    }
    return format(date, formatDateStripTime(dateFormat || defaultDateFormat))
  }
  if (!Date.parse(date)) {
    return date
  } else {
    date = utcToZonedTime(date).toString()
  }

  if (withTime) {
    return format(new Date(date), userFormatDate)
  }
  return format(new Date(date), formatDateStripTime(dateFormat || defaultDateFormat))
}

export const formatDateStripTime = (dateTimeFormat: string): string => {
  if (dateTimeFormat) {
    const [selectedDateValue] = dateTimeFormat.split(' ')
    return selectedDateValue || dateTimeFormat
  }

  return dateTimeFormat
}

export const formatUTCDate = (
  date: string | null | undefined,
  withTime?: boolean
): string | null => {
  const dateFormat = localStorage.getItem(PREFERRED_DATE_FORMAT)
  const userFormatDate = getDateTimeFormat()

  if (!date) {
    return null
  }
  if (!Date.parse(date)) {
    return date
  }

  if (withTime) {
    return format(convertDateToUTC(new Date(date)), userFormatDate)
  }
  return format(
    convertDateToUTC(new Date(date)),
    formatDateStripTime(dateFormat || defaultDateFormat)
  )
}

export const getDateTimeFormat = (): string => {
  const userFormatDate = localStorage.getItem(PREFERRED_DATE_FORMAT) || defaultDateFormat
  const timeFormatIncluded = /HH|mm|ss/.test(userFormatDate)

  return timeFormatIncluded ? userFormatDate : `${userFormatDate} ${defaultTimeFormat}`
}

export const dateComparator = (
  a: string | null | undefined,
  b: string | undefined | null,
  revert?: boolean
): 0 | 1 | -1 => {
  if (!a) {
    return -1
  }

  if (!b) {
    return 1
  }

  const dateA = parseISO(a)
  const dateB = parseISO(b)

  if (revert) {
    if (isBefore(dateA, dateB)) {
      return 1
    }

    if (isAfter(dateA, dateB)) {
      return -1
    }
  } else {
    if (isBefore(dateA, dateB)) {
      return -1
    }

    if (isBefore(dateA, dateB)) {
      return 1
    }
  }
  return 0
}

export const formatTimezoneAgnosticDate = (dateStr: string, isDateOnly?: boolean): string => {
  if (!dateStr) {
    return dateStr
  }
  const dateFormat = getDateTimeFormat()
  if (isDateOnly) {
    return format(dateStr, formatDateStripTime(dateFormat))
  }
  return format(dateStr, dateFormat)
}

export const getOneYearFromCurrentDate = () =>
  new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()

export const LAST_30_DAYS: [Date, Date] = [startOfDay(subDays(new Date(), 30)), endOfYesterday()]
export const LAST_90_DAYS: [Date, Date] = [startOfDay(subDays(new Date(), 90)), endOfYesterday()]
export const LAST_180_DAYS: [Date, Date] = [startOfDay(subDays(new Date(), 180)), endOfYesterday()]
export const LAST_30_DAYS_INCLUDING_TODAY: [Date, Date] = [
  startOfDay(subDays(new Date(), 30)),
  endOfToday(),
]

export const stripTimeFromFormat = (format?: string): string | undefined =>
  format ? format.replace(/[Hhms:]/g, '').trim() : undefined
